import { getCookie } from 'cookies-next';
import type { IncomingMessage, ServerResponse } from 'http';

import { appLogger } from '@/utils/app-logger/appLogger';
import { DEVICE_ID_COOKIE, LEGACY_CLIENT_ID_COOKIE } from '@/utils/constants/common';

// All-zero ids the platform returns when it has no real device. Never valid as fpid.
const PLACEHOLDER_DEVICE_IDS = new Set([
    '00000000-0000-0000-0000-000000000000',
    '00000000-0000-4000-8000-000000000000',
]);

/** True if the id is the all-zero placeholder. */
export function isPlaceholderDeviceId(deviceId?: string | null): boolean {
    return (
        typeof deviceId === 'string' && PLACEHOLDER_DEVICE_IDS.has(deviceId.trim().toLowerCase())
    );
}

export type ResolvedDeviceId = {
    /** Id to send, or undefined so the platform creates one. */
    deviceId?: string;
    /** 'v1v2' only when migrating a legacy id. */
    updateType?: 'v1v2';
};

/**
 * Picks the id to send to start/restart. The client never creates one.
 * V2 cookie -> send it. Legacy only -> send it with v1v2. Nothing valid -> send none.
 */
export function resolveDeviceIdToSend(
    req?: IncomingMessage,
    res?: ServerResponse,
): ResolvedDeviceId {
    const cookieOptions = req && res ? { req, res } : undefined;
    const legacyClientId = getCookie(LEGACY_CLIENT_ID_COOKIE, cookieOptions) as string | undefined;
    const existingDeviceId = getCookie(DEVICE_ID_COOKIE, cookieOptions) as string | undefined;

    if (
        existingDeviceId &&
        typeof existingDeviceId === 'string' &&
        !isPlaceholderDeviceId(existingDeviceId)
    ) {
        appLogger.info('Device id resolved: reusing existing V2 id', {
            component: 'resolveDeviceIdToSend',
            decision: 'existing-v2',
        });
        return { deviceId: existingDeviceId };
    }

    if (
        legacyClientId &&
        typeof legacyClientId === 'string' &&
        !isPlaceholderDeviceId(legacyClientId)
    ) {
        appLogger.info('Device id resolved: migrating legacy id', {
            component: 'resolveDeviceIdToSend',
            decision: 'legacy-v1v2',
        });
        return { deviceId: legacyClientId, updateType: 'v1v2' };
    }

    // All-zero cookie: send none so the platform makes a new one.
    if (isPlaceholderDeviceId(existingDeviceId)) {
        appLogger.warn('Device cookie is the all-zero placeholder; sending none', {
            component: 'resolveDeviceIdToSend',
        });
    }

    appLogger.info('Device id resolved: sending none, platform will create one', {
        component: 'resolveDeviceIdToSend',
        decision: 'none',
    });
    return {};
}
